import React from 'react'
import cn from 'clsx'
import noop from 'lib/noop'
import { OptimizelyClientSideExperiments, useOptimizelyExperiment } from 'lib/optimizely'
import { useTranslation } from 'react-i18next'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import styled from 'styled-components'
import TextButton from 'components/Luxkit/Button/TextButton'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

const FooterButtons = styled.div`
  display: grid;
  grid-template-columns: 50% 50%;
  grid-gap: ${rem(8)};
`

const StyledModalContent = styled(ModalContent)`
  height: 100%;

  &.is-variant {
    padding-left: 0;
    padding-right: 0;

    ${mediaQueryUp.md} {
      padding-left: ${rem(24)};
      padding-right: ${rem(24)};
    }
  }
`
interface Props {
  desktop?: boolean;
  isOpen: boolean;
  onClose?: () => void;
  onNoThanks?: () => void;
  onUpgrade?: () => void;
  children: React.ReactNode;
}

function PackageUpgradeModal({
  desktop = false,
  isOpen,
  onClose,
  onNoThanks,
  onUpgrade,
  children,
}: Props) {
  const { t } = useTranslation('offerPage')

  const isVariantEnabled = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.upgradeModalVariant)

  const hidePrimaryAction = isVariantEnabled || !desktop
  const hideSecondaryAction = (isVariantEnabled && desktop) || (!isVariantEnabled && !desktop)
  const hideModalFooter = isVariantEnabled && desktop
  const showCloseButton = !isVariantEnabled && !desktop

  return (
    <ModalBase
        isOpen={isOpen}
        size={desktop ? 'large' : 'small'}
        onClose={onClose}
      >
      <ModalHeader
          title={t('wantToUpgragdeYourPackage', { ns: 'offerPage' })}
          subtitle={t('payALittleExtra', { ns: 'offerPage' })}
          onCloseButtonClick={showCloseButton ? onClose : undefined}
        />
      <ModalBody>
        <StyledModalContent className={cn({ 'is-variant': isVariantEnabled })}>
          {children}
        </StyledModalContent>
      </ModalBody>
      {!hideModalFooter && (
        <ModalFooter
            isPrimaryActionHidden={hidePrimaryAction}
            isSecondaryActionHidden={hideSecondaryAction}
            primaryActionProps={{
              children: t('Upgrade', { ns: 'offerPage' }),
              onClick: onUpgrade,
            }}
            secondaryActionProps={{
              children: isVariantEnabled ? t('Keep my current package', { ns: 'offerPage' }) : t('No thanks', { ns: 'offerPage' }),
              onClick: onNoThanks,
            }}
          >
          {!isVariantEnabled && !desktop && (
            <FooterButtons>
              <TextButton kind="tertiary" size="large" onClick={onNoThanks}>{t('No thanks', { ns: 'offerPage' })}</TextButton>
              <TextButton kind="primary" size="large" onClick={onUpgrade}>{t('Upgrade', { ns: 'offerPage' })}</TextButton>
            </FooterButtons>
          )}
        </ModalFooter>
      )}
    </ModalBase>
  )
}

PackageUpgradeModal.defaultProps = {
  onClose: noop,
  onNoThanks: noop,
  onUpgrade: noop,
}

export default PackageUpgradeModal
